import {
  json,
  useLoaderData,
  useNavigate,
  useParams,
  useSearchParams,
} from "@remix-run/react";
import { useContext, useState } from "react";
import { OrderSource } from "~/entities/orderEntities";
import PageWrapper from "~/components/PageWrapper/PageWrapper";
import ThemeContext from "~/contexts/ThemeContext";
import { urlGen } from "~/helpers/routesHelpers";
import { SiteListObject } from "~/entities/siteEntities";
import SitesList from "~/modules/sites/components/SitesList/SitesList";
import StoreFinder from "~/modules/sites/components/StoreFinder/StoreFinder";
import {
  deliverySearchEnabled,
  storeFinderEnabled,
} from "~/helpers/storeFinderGate";
import Api from "~/api/api";
import { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import OrderContext from "~/contexts/OrderContext";
import { findOrderTypeForFulfilment } from "~/helpers/storeFinderFulfilment";
import BaseLayout from "~/layouts/BaseLayout/BaseLayout";
import { iconMeta } from "~/helpers/remixHelpers";
import { getPersistentSession } from "~/sessions";

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  return [
    iconMeta(matches),
    {
      title: `${
        data?.orgSites?.domainSettings?.title
          ? data?.orgSites?.domainSettings?.title
          : data?.orgSites?.orgName
      } - Sites`,
    },
    {
      name: "description",
      content: data?.orgSites?.domainSettings?.description
        ? `${data?.orgSites?.domainSettings?.description} - Choosing a site for organisation ${data?.orgSites?.orgName}`
        : `Choosing a site for organisation ${data?.orgSites?.orgName}`,
    },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const orgSites = await Api.Site.getOrgSites(request);

  const persistentSession = await getPersistentSession(
    request.headers.get("Cookie"),
  );
  let prevSiteId = persistentSession.get("prevSite");

  if (!orgSites.sites.find((x) => x.id === prevSiteId)) {
    prevSiteId = undefined;
  }

  // The remembered address travels with prevSite, and it carries coordinates —
  // so the store list can sort by distance without geocoding anything. The
  // localStorage copy SitesList used to read was a bare string, which is why it
  // had to geocode it back into a centre on every mount.
  const rememberedAddress = persistentSession.get("deliveryAddress") ?? null;

  return json({
    orgSites,
    prevSiteId,
    rememberedAddress,
  });
}

const Page = () => {
  const { orgSites, prevSiteId, rememberedAddress } =
    useLoaderData<typeof loader>();

  const { template } = useContext(ThemeContext);
  const { orderSource: contextOrderSource } = useContext(OrderContext);
  // /sites?experience=catering (where /catering sends customers) opens the catering picker; the finder can also
  // switch between everyday and catering in place
  const [searchParams] = useSearchParams();
  const [orderSource, setOrderSource] = useState<OrderSource>(
    searchParams.get("experience") === "catering"
      ? OrderSource.CATERING
      : contextOrderSource,
  );
  const catering = orderSource === OrderSource.CATERING;

  const navigate = useNavigate();

  const params = useParams();

  const goToOrder = (
    site: SiteListObject,
    orderTypeCode?: string | null,
    deliveryAddress?: {
      formattedAddress: string;
      latitude: number;
      longitude: number;
    },
  ) => {
    navigate(
      urlGen("Order", {
        orderSource,
        siteId: site.id,
        tableNumber: params.table,
        orderTypeCode: orderTypeCode,
        // Carried on the URL because no order exists yet to hold it. The
        // destination loader moves it into the session's deliveryInfo; without
        // this the address chosen on /sites is silently lost and PlaceOrder
        // rejects the order for having no delivery coordinates.
        deliveryAddress: deliveryAddress?.formattedAddress ?? null,
        deliveryLat: deliveryAddress?.latitude ?? null,
        deliveryLng: deliveryAddress?.longitude ?? null,
      }),
    );
  };

  // Style
  const backgroundColor = template.storesScreen.general.backgroundColour;

  return (
    <BaseLayout orgId={orgSites.id}>
      <PageWrapper
        loading={false}
        style={{ backgroundColor, padding: "0 16px" }}
      >
        {storeFinderEnabled(template) ? (
          /* PageWrapper shrink-wraps its children, so give the finder a real
             centred column — otherwise it collapses to its content and the
             delivery address input is cramped. */
          <div
            style={{
              width: "100%",
              maxWidth: catering ? 760 : 480,
              margin: "24px auto 0",
            }}
          >
            {catering && (
              <h1
                style={{ margin: "0 0 16px", fontSize: 24, lineHeight: "30px" }}
              >
                Choose a catering store
              </h1>
            )}
            <StoreFinder
              orgId={orgSites.id}
              sites={orgSites.sites}
              prevSiteId={prevSiteId}
              orderSource={orderSource}
              onExperienceChange={setOrderSource}
              deliverySearch={deliverySearchEnabled(template)}
              initialAddress={
                rememberedAddress?.address1 &&
                rememberedAddress.latitude != null &&
                rememberedAddress.longitude != null
                  ? {
                      formattedAddress: rememberedAddress.address1,
                      latitude: rememberedAddress.latitude,
                      longitude: rememberedAddress.longitude,
                    }
                  : undefined
              }
              onSelect={({ site, fulfilment, deliveryAddress }) => {
                // The finder speaks PICKUP / DELIVERY; this experience's canonical type for it
                const orderTypeCode = findOrderTypeForFulfilment(
                  site.orderTypes ?? [],
                  fulfilment,
                  orderSource,
                )?.code;
                goToOrder(site, orderTypeCode, deliveryAddress);
              }}
            />
          </div>
        ) : (
          <SitesList
            sites={orgSites.sites}
            onSelectSite={goToOrder}
            prevSiteId={prevSiteId}
          />
        )}
      </PageWrapper>
    </BaseLayout>
  );
};

export default Page;
